<template>
  <q-skeleton
    bordered
    class="accounting-provider without-space"
    :animation="isSyncing ? undefined : 'none'"
    :class="{'syncing': isSyncing, 'error': hasError, 'compact-mode': compactMode}"
    :style="{'background-color': backgroundColor}"
  >
    <!-- Compact/Narrow Layout (for control panels, narrow views) -->
    <template v-if="compactMode">
      <!-- Header with Icon and Provider Name -->
      <div class="row items-center q-pa-sm q-pb-xs">
        <div v-if="details.current_provider" class="q-mr-sm">
          <SyncSpinner v-if="isSyncing" />
          <q-icon
            v-else-if="hasError"
            name="mdi-exclamation"
            size="2em"
            color="red-8"
          />
          <ProviderIcon
            v-else
            :driver="details.current_provider"
          />
        </div>
        <div class="col text-caption text-weight-bold ellipsis">
          {{ details.current_provider_name || 'Accounting Provider' }}
        </div>
        <q-btn
          v-if="!isSyncing && details.current_provider && (canManageProvider || showOpenExternalBtn || showAccountingId)"
          flat
          size="sm"
          icon="more_vert"
          round
          dense
          class="q-ml-xs"
        >
          <q-menu>
            <q-list
              dense
              padding
              style="min-width: 250px;"
            >
              <template v-if="showAccountingId">
                <q-item-wrapper
                  :label="`${details.current_provider_name} ID`"
                  :caption="details.accounting_id"
                />
                <q-separator
                  v-if="canManageProvider || showOpenExternalBtn"
                  inset
                  spaced
                />
              </template>
              <q-item-wrapper
                v-if="showOpenExternalBtn"
                v-close-popup
                clickable
                icon-right="mdi-open-in-new"
                :dense="!$q.platform.has.touch"
                :label="`View on ${details.current_provider_name}`"
                @click="goToHref(details.external_url)"
              />
              <q-item-wrapper
                v-if="canManageProvider"
                v-close-popup
                clickable
                label="Provider Settings"
                icon-right="mdi-cog"
                :dense="!$q.platform.has.touch"
                @click="goTo('settings/accountingProvider')"
              />
            </q-list>
          </q-menu>
        </q-btn>
      </div>

      <!-- Status Message -->
      <div class="q-px-sm q-pb-xs">
        <div class="text-caption text-weight-medium">
          <span v-if="isPendingSync" class="text-orange-10">
            Queued for sync...
          </span>
          <template v-else-if="isSyncing">
            Syncing...
          </template>
          <template v-else-if="details.accounting_id && details.current_provider && details.last_sync_time">
            Last synced {{ $formatSystemDate(details.last_sync_time, luxonFormats.FRIENDLY_DATE_TIME) }}
          </template>
          <span v-else-if="!details.accounting_id && details.current_provider" class="text-red-8">
            Not linked with {{ details.current_provider_name }}
          </span>
        </div>
      </div>

      <!-- Sync Button (Full Width, Large) -->
      <div v-if="!isSyncing && details.current_provider && showSyncBtn" class="q-px-sm q-pb-sm">
        <q-btn
          icon="mdi-sync"
          label="Sync Now"
          color="primary"
          size="md"
          no-caps
          unelevated
          class="full-width"
          @click="confirmSync"
        />
      </div>
    </template>

    <!-- Wide/Normal Layout (for dialogs, wider views) -->
    <template v-else>
      <q-item class="q-px-sm">
        <q-item-section v-if="details.current_provider" side>
          <SyncSpinner v-if="isSyncing" />
          <q-icon
            v-else-if="hasError"
            name="mdi-exclamation"
            size="3em"
          />
          <ProviderIcon
            v-else
            :driver="details.current_provider"
          />
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-bold">
            {{ connectedViaLabel }}
          </q-item-label>
          <q-item-label
            caption
            class="text-bold"
          >
            <span v-if="isPendingSync" class="text-orange-10">
              Item is queued and will be synced soon. Please wait...
            </span>
            <template v-else-if="isSyncing">
              Item is syncing. Please wait...
            </template>
            <template v-else-if="details.accounting_id && details.current_provider && details.last_sync_time">
              Last Synced: {{ $formatSystemDate(details.last_sync_time, luxonFormats.FRIENDLY_DATE_TIME) }}
            </template>
            <span v-else-if="!details.accounting_id && details.current_provider" class="text-red-8">
              Item has not been linked with {{ details.current_provider_name }}
            </span>
          </q-item-label>
        </q-item-section>

        <template v-if="!isSyncing && details.current_provider">
          <q-item-section v-if="showSyncBtn" side>
            <q-btn
              icon-right="mdi-sync"
              label="Sync"
              color="blue"
              size="12px"
              @click="confirmSync"
            />
          </q-item-section>

          <q-item-section side>
            <q-btn
              v-if="canManageProvider || showOpenExternalBtn || showAccountingId"
              flat
              size="12px"
              icon="more_vert"
              round
              dense
            >
              <q-menu>
                <q-list padding style="min-width: 275px;">
                  <template v-if="showAccountingId">
                    <q-item-wrapper
                      :label="`${details.current_provider_name} Accounting ID`"
                      :caption="details.accounting_id"
                    />
                    <q-separator
                      v-if="canManageProvider || showOpenExternalBtn"
                      inset
                      spaced
                    />
                  </template>
                  <q-item-wrapper
                    v-if="showOpenExternalBtn"
                    v-close-popup
                    clickable
                    icon-right="mdi-open-in-new"
                    :dense="!$q.platform.has.touch"
                    :label="`View on ${details.current_provider_name}`"
                    @click="goToHref(details.external_url)"
                  />
                  <q-item-wrapper
                    v-if="canManageProvider"
                    v-close-popup
                    clickable
                    label="Accounting Provider Settings"
                    icon-right="mdi-cog"
                    :dense="!$q.platform.has.touch"
                    @click="goTo('settings/accountingProvider')"
                  />
                </q-list>
              </q-menu>
            </q-btn>
          </q-item-section>
        </template>
      </q-item>
    </template>

    <AccountingError
      v-if="hasError"
      :error="details.last_error"
    />
  </q-skeleton>
</template>

<script>
import ProviderIcon from 'components/app/settings/providers/partials/ProviderIcon'
import SyncSpinner from 'components/app/settings/providers/partials/SyncSpinner'
import AccountingError from './AccountingError'

/**
 * Display provider details for an accounting model
 */
export default {
  components: {
    ProviderIcon, SyncSpinner, AccountingError
  },
  props: {
    details: {
      type: Object,
      required: true
    },
    forceMobileMode: {
      type: Boolean,
      default: false
    },
    // Show button at all times to allow this item to sync
    canSync: {
      type: Boolean,
      default: false
    },
    // Only allow syncing of this item if it doesn't already have an accounting ID
    onlyAllowFirstSync: {
      type: Boolean,
      default: false
    }
  },
  emits: ['sync'],
  computed: {
    backgroundColor () {
      return this.changeColorBrightness(this.details.current_provider_color, 90)
    },
    isSyncing () {
      return this.details.is_syncing
    },
    isPendingSync () {
      return !this.isSyncing && this.details.is_pending_sync
    },
    hasError () {
      return !this.isSyncing && this.details.last_error
    },
    canManageProvider () {
      return this.can(this.permissions.canManageProviders)
    },
    compactMode () {
      // Use compact mode if forced, or if window is narrow (less than 500px)
      return this.forceMobileMode || this.windowWidth < 500
    },
    showSyncBtn () {
      return !this.isPendingSync &&
             (this.canSync || (this.onlyAllowFirstSync && !this.details.accounting_id)) &&
             this.canManageProvider &&
             !this.isSyncing &&
             !!this.details.current_provider
    },
    showAccountingId () {
      return this.canManageProvider && !!this.details.accounting_id
    },
    showOpenExternalBtn () {
      return this.canManageProvider && !!this.details.external_url
    },
    connectedViaLabel () {
      if (this.isSyncing && this.details.current_provider_name) {
        return 'Syncing with ' + this.details.current_provider_name
      }
      let str = 'Created on ' + this.details.created_from_name
      if (this.details.created_from !== this.details.current_provider && this.details.accounting_id) {
        str += ', syncing with ' + this.details.current_provider_name
      }
      return str
    }
  },
  methods: {
    confirmSync () {
      this.$q.dialog({
        title: 'Confirm',
        message: 'Are you sure you want to sync this item?',
        ok: true,
        cancel: true
      })
        .onOk(() => {
          this.details.is_pending_sync = true
          this.$emit('sync')
        })
    }
  }
}
</script>
